import { useState, useEffect, useRef, useCallback, useMemo, useId } from 'react';
import {
  MessageCircleQuestion,
  X,
  Search,
  ArrowLeft,
  ChevronRight,
  Sparkles,
  MapPin,
  ListChecks,
  Lightbulb,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  categorizedForVariant,
  categoriesForVariant,
  entriesForVariant,
  searchKnowledge,
  type Category,
  type KnowledgeEntry,
} from './knowledge';

/**
 * BestAccoAssistant — an in-app help center for BestAcco.
 *
 * IMPORTANT: this is a *curated, client-side* help browser, not a chatbot. All
 * content comes from the local knowledge base in `knowledge.ts`. There is no LLM
 * / external API call, so it is self-contained and works offline.
 *
 * The primary experience is browsing: topics are grouped by category and the
 * user drills into a topic to read the guide. A search box at the top filters
 * topics as a convenience. It is rendered once per shell — the PMS `AppLayout`
 * (variant="pms") and the platform `AdminLayout` (variant="admin") — and each
 * variant only shows topics in its scope (pms+both / admin+both) and is tinted
 * on-brand.
 */

type Variant = 'pms' | 'admin';

interface VariantTheme {
  /** Header + trigger background. */
  headerClass: string;
  /** Trigger button background. */
  triggerClass: string;
  /** Category label / accent text colour. */
  accentText: string;
  /** Focus ring used on interactive controls. */
  ring: string;
  /** Hover background for a topic row. */
  rowHover: string;
}

const THEMES: Record<Variant, VariantTheme> = {
  // PMS hotel shell — white app, primary (teal/blue) accent.
  pms: {
    headerClass: 'bg-primary-600',
    triggerClass: 'bg-primary-600 hover:bg-primary-700',
    accentText: 'text-primary-700',
    ring: 'focus-visible:ring-primary-500',
    rowHover: 'hover:bg-primary-50',
  },
  // Platform console — deep BestAcco blue.
  admin: {
    headerClass: 'bg-gradient-to-b from-[#003580] to-[#002255]',
    triggerClass: 'bg-gradient-to-b from-[#003580] to-[#002255] hover:brightness-110',
    accentText: 'text-[#003580]',
    ring: 'focus-visible:ring-brand-500',
    rowHover: 'hover:bg-blue-50',
  },
};

/** Split a knowledge answer into paragraphs for readable rendering. */
function toParagraphs(text: string): string[] {
  return text.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
}

export function BestAccoAssistant({ variant = 'pms' }: { variant?: Variant }) {
  const theme = THEMES[variant];

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<Category | 'all'>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const panelId = useId();
  const headingId = `${panelId}-heading`;

  // Topics in scope for this shell, and the same grouped by category.
  const inScope = useMemo(() => entriesForVariant(variant), [variant]);
  const grouped = useMemo(() => categorizedForVariant(variant), [variant]);
  const categories = useMemo(() => categoriesForVariant(variant), [variant]);

  // Fast id → entry lookup (used to resolve "related topics" links).
  const byId = useMemo(() => {
    const m = new Map<string, KnowledgeEntry>();
    for (const e of inScope) m.set(e.id, e);
    return m;
  }, [inScope]);

  const selected = useMemo(
    () => (selectedId ? byId.get(selectedId) ?? null : null),
    [selectedId, byId],
  );

  // Related entries that are actually in scope for this shell, in listed order.
  const relatedEntries = useMemo((): KnowledgeEntry[] => {
    if (!selected?.related) return [];
    return selected.related
      .map((id) => byId.get(id))
      .filter((e): e is KnowledgeEntry => Boolean(e));
  }, [selected, byId]);

  // Search results: score across the KB, then keep only in-scope entries. The
  // active category chip narrows the browse/search further to one topic area.
  const filteredGroups = useMemo((): { category: Category; entries: KnowledgeEntry[] }[] => {
    const q = query.trim();
    let groups = grouped;
    if (q) {
      const inScopeIds = new Set(inScope.map((e) => e.id));
      const hits = searchKnowledge(q)
        .map((r) => r.entry)
        .filter((e) => inScopeIds.has(e.id));
      // Regroup the hits by category, preserving category order.
      groups = grouped
        .map((g) => ({
          category: g.category,
          entries: hits.filter((e) => e.category === g.category),
        }))
        .filter((g) => g.entries.length > 0);
    }
    if (activeCategory !== 'all') {
      groups = groups.filter((g) => g.category === activeCategory);
    }
    return groups;
  }, [query, grouped, inScope, activeCategory]);

  const totalHits = useMemo(
    () => filteredGroups.reduce((n, g) => n + g.entries.length, 0),
    [filteredGroups],
  );

  const close = useCallback(() => {
    setIsOpen(false);
    requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  const openTopic = useCallback((id: string) => {
    setSelectedId(id);
    requestAnimationFrame(() => {
      if (bodyRef.current) bodyRef.current.scrollTop = 0;
      // Move focus to the panel body so the reader lands on the guide.
      bodyRef.current?.focus();
    });
  }, []);

  const backToBrowse = useCallback(() => {
    setSelectedId(null);
    requestAnimationFrame(() => searchRef.current?.focus());
  }, []);

  // Focus the search box when the panel opens.
  useEffect(() => {
    if (isOpen && !selectedId) {
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [isOpen, selectedId]);

  // Esc to close (or, in a topic, go back) + focus trap within the panel.
  useEffect(() => {
    if (!isOpen) return undefined;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (selectedId) backToBrowse();
        else close();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedId, close, backToBrowse]);

  // Close when clicking outside the panel (but not the trigger).
  useEffect(() => {
    if (!isOpen) return undefined;
    function handlePointer(e: MouseEvent) {
      const target = e.target as Node;
      if (
        panelRef.current &&
        !panelRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointer);
    return () => document.removeEventListener('mousedown', handlePointer);
  }, [isOpen]);

  const scopeLabel = (scope: KnowledgeEntry['scope']): string | null => {
    if (scope === 'both') return 'PMS + Console';
    if (variant === 'admin' && scope === 'admin') return null; // implicit here
    if (variant === 'pms' && scope === 'pms') return null; // implicit here
    return scope === 'admin' ? 'Console' : 'PMS';
  };

  return (
    <div className="fixed bottom-5 right-5 z-[70] print:hidden">
      {/* Help panel */}
      {isOpen && (
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          aria-labelledby={headingId}
          className={cn(
            'absolute bottom-16 right-0 w-[min(94vw,25rem)] h-[min(80vh,36rem)]',
            'flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl',
          )}
        >
          {/* Header */}
          <div className={cn('flex items-center justify-between gap-3 px-4 py-3 shrink-0', theme.headerClass)}>
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles size={18} className="text-white/90 shrink-0" aria-hidden="true" />
              <div className="min-w-0">
                <h2 id={headingId} className="text-sm font-semibold text-white leading-tight">
                  BestAcco Help Center
                </h2>
                <p className="text-[11px] text-white/70">Browse topics, grounded in your product</p>
              </div>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Close help center"
              className={cn(
                'p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
              )}
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>

          {selected ? (
            /* ─── Topic detail ─────────────────────────────────────────── */
            <>
              <div className="shrink-0 border-b border-gray-100 px-3 py-2.5">
                <button
                  type="button"
                  onClick={backToBrowse}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-gray-600',
                    'hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2',
                    theme.ring,
                  )}
                >
                  <ArrowLeft size={14} aria-hidden="true" />
                  All topics
                </button>
              </div>
              <div
                ref={bodyRef}
                tabIndex={-1}
                className="flex-1 overflow-y-auto px-4 py-4 focus:outline-none"
              >
                <p className={cn('text-[11px] font-semibold uppercase tracking-wide', theme.accentText)}>
                  {selected.category}
                </p>
                <h3 className="mt-1 text-base font-semibold text-gray-900 leading-snug">
                  {selected.question}
                </h3>

                {selected.summary && (
                  <p className="mt-2 text-[13px] font-medium leading-relaxed text-gray-600">
                    {selected.summary}
                  </p>
                )}

                {selected.where && (
                  <div
                    className={cn(
                      'mt-3 inline-flex items-start gap-1.5 rounded-lg bg-gray-50 px-2.5 py-1.5',
                      'text-[12px] font-medium text-gray-600 ring-1 ring-inset ring-gray-200',
                    )}
                  >
                    <MapPin size={14} className={cn('mt-px shrink-0', theme.accentText)} aria-hidden="true" />
                    <span>
                      <span className="text-gray-400">Where: </span>
                      {selected.where}
                    </span>
                  </div>
                )}

                <div className="mt-3 space-y-3">
                  {toParagraphs(selected.answer).map((para, i) => (
                    <p
                      key={i}
                      className="whitespace-pre-line text-[13px] leading-relaxed text-gray-700"
                    >
                      {para}
                    </p>
                  ))}
                </div>

                {selected.steps && selected.steps.length > 0 && (
                  <div className="mt-4">
                    <p className={cn('flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide', theme.accentText)}>
                      <ListChecks size={13} aria-hidden="true" />
                      Step by step
                    </p>
                    <ol className="mt-2 space-y-2">
                      {selected.steps.map((step, i) => (
                        <li key={i} className="flex gap-2.5 text-[13px] leading-relaxed text-gray-700">
                          <span
                            className={cn(
                              'mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white',
                              theme.triggerClass,
                            )}
                            aria-hidden="true"
                          >
                            {i + 1}
                          </span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {selected.tip && (
                  <div className="mt-4 flex gap-2 rounded-xl bg-amber-50 px-3 py-2.5 ring-1 ring-inset ring-amber-200">
                    <Lightbulb size={15} className="mt-px shrink-0 text-amber-500" aria-hidden="true" />
                    <p className="text-[12.5px] leading-relaxed text-amber-900">
                      <span className="font-semibold">Tip. </span>
                      {selected.tip}
                    </p>
                  </div>
                )}

                {relatedEntries.length > 0 && (
                  <div className="mt-5 border-t border-gray-100 pt-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      Related topics
                    </p>
                    <ul className="mt-1.5">
                      {relatedEntries.map((rel) => (
                        <li key={rel.id}>
                          <button
                            type="button"
                            onClick={() => openTopic(rel.id)}
                            className={cn(
                              'group flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors',
                              'focus-visible:outline-none focus-visible:ring-2',
                              theme.rowHover,
                              theme.ring,
                            )}
                          >
                            <span className="flex-1 text-[13px] leading-snug text-gray-700">
                              {rel.question}
                            </span>
                            <ChevronRight
                              size={15}
                              className="shrink-0 text-gray-300 group-hover:text-gray-400"
                              aria-hidden="true"
                            />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* ─── Browse + search ──────────────────────────────────────── */
            <>
              {/* Search box */}
              <div className="shrink-0 border-b border-gray-100 px-3 py-2.5">
                <div className="relative">
                  <Search
                    size={15}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    aria-hidden="true"
                  />
                  <input
                    ref={searchRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search help topics…"
                    aria-label="Search help topics"
                    className={cn(
                      'w-full rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-9 py-2 text-sm text-gray-900',
                      'placeholder-gray-400 outline-none focus-visible:ring-2 focus-visible:border-transparent',
                      theme.ring,
                    )}
                  />
                  {query && (
                    <button
                      type="button"
                      onClick={() => setQuery('')}
                      aria-label="Clear search"
                      className={cn(
                        'absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400',
                        'hover:text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2',
                        theme.ring,
                      )}
                    >
                      <X size={14} aria-hidden="true" />
                    </button>
                  )}
                </div>

                {/* Topic-wise category filter chips */}
                <div
                  className="mt-2 flex gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                  role="group"
                  aria-label="Filter topics by category"
                >
                  {(['all', ...categories] as (Category | 'all')[]).map((cat) => {
                    const isActive = activeCategory === cat;
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setActiveCategory(cat)}
                        aria-pressed={isActive}
                        className={cn(
                          'shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors',
                          'focus-visible:outline-none focus-visible:ring-2',
                          theme.ring,
                          isActive
                            ? cn('text-white', theme.triggerClass)
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                        )}
                      >
                        {cat === 'all' ? 'All topics' : cat}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Category / topic list */}
              <div ref={bodyRef} className="flex-1 overflow-y-auto px-2 py-2" aria-live="polite">
                {query.trim() && (
                  <p className="px-2 py-1.5 text-[11px] text-gray-500">
                    {totalHits === 0
                      ? 'No topics match — try a different word, or clear the search to browse.'
                      : `${totalHits} topic${totalHits === 1 ? '' : 's'} match "${query.trim()}"`}
                  </p>
                )}

                {filteredGroups.map((group) => (
                  <section key={group.category} className="mb-1.5">
                    <h3
                      className={cn(
                        'px-2 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide',
                        theme.accentText,
                      )}
                    >
                      {group.category}
                    </h3>
                    <ul>
                      {group.entries.map((entry) => {
                        const badge = scopeLabel(entry.scope);
                        return (
                          <li key={entry.id}>
                            <button
                              type="button"
                              onClick={() => openTopic(entry.id)}
                              className={cn(
                                'group flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left transition-colors',
                                'focus-visible:outline-none focus-visible:ring-2',
                                theme.rowHover,
                                theme.ring,
                              )}
                            >
                              <span className="flex-1 text-[13px] leading-snug text-gray-700">
                                {entry.question}
                              </span>
                              {badge && (
                                <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                                  {badge}
                                </span>
                              )}
                              <ChevronRight
                                size={15}
                                className="shrink-0 text-gray-300 group-hover:text-gray-400"
                                aria-hidden="true"
                              />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-label={isOpen ? 'Close BestAcco Help Center' : 'BestAcco Help Center'}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        className={cn(
          'flex h-12 w-12 items-center justify-center rounded-full text-white shadow-lg transition-all',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          theme.triggerClass,
          theme.ring,
        )}
      >
        {isOpen ? (
          <X size={22} aria-hidden="true" />
        ) : (
          <MessageCircleQuestion size={24} aria-hidden="true" />
        )}
      </button>
    </div>
  );
}
